import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Header } from '../../../widgets/header'
import { useAuth } from '../../../features/auth'
import { useGallery } from '../model/useGallery'
import { useFeed } from '../model/useFeed'
import { useEventVideos } from '../model/useEventVideos'
import { deletePost } from '../api/galleryApi'
import { buildCdnUrl, isVideoUrl, useInfiniteScroll, handleImageError } from '../../../shared/lib'
import { ProfileImage } from '../../../shared/ui'
import type { GalleryCategory, GalleryAlbum, GalleryPhotoItem, ViewMode, SubCategory, SubCategoryChild, FeedPost, FeedPostImage, ChurchOption, EventVideo, EventVideoGroup } from '../model/types'

const CATEGORIES: { key: GalleryCategory; label: string }[] = [
  { key: 'ALL', label: '전체' },
  { key: 'RETREAT', label: '수련회' },
  { key: 'CHURCH', label: '성전별' },
  { key: 'VIDEO', label: '영상' },
]

// ─── Icons ───────────────────────────────────────────────

const GridIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={active ? '#FFFFFF' : '#666666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
)

const FeedIcon = ({ active }: { active: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={active ? '#FFFFFF' : '#666666'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
)

const HeartIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E74C6F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
)

const CommentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
)

const ShareIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
)

const BookmarkIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
)

// ─── View Toggle ─────────────────────────────────────────

const ViewToggle = ({ viewMode, onChange }: { viewMode: ViewMode; onChange: (mode: ViewMode) => void }) => (
  <div className="flex items-center bg-[#F0F0F0] rounded-lg p-1 gap-1 shrink-0">
    <button
      onClick={() => onChange('grid')}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-all duration-200 ${
        viewMode === 'grid' ? 'bg-[#3B5BDB] text-white font-semibold' : 'text-[#666666] hover:bg-gray-200'
      }`}
    >
      <GridIcon active={viewMode === 'grid'} />
      갤러리
    </button>
    <button
      onClick={() => onChange('feed')}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-all duration-200 ${
        viewMode === 'feed' ? 'bg-[#3B5BDB] text-white font-semibold' : 'text-[#666666] hover:bg-gray-200'
      }`}
    >
      <FeedIcon active={viewMode === 'feed'} />
      피드
    </button>
  </div>
)

// ─── Lazy Image with skeleton + fade-in ──────────────────

const LazyImage = ({
  src,
  alt,
  className,
  imgClassName,
  onClick,
}: {
  src: string
  alt: string
  className?: string
  imgClassName?: string
  onClick?: () => void
}) => {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    setLoaded(false)
  }, [src])

  useEffect(() => {
    // 이미 캐시된 이미지는 즉시 표시
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [src])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div
      className={`relative overflow-hidden ${className ?? ''}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? alt : undefined}
    >
      {!loaded && (
        <div className="absolute inset-0 bg-[#E8E8E8] animate-pulse" />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`${imgClassName ?? ''} transition-[opacity,transform] duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          handleImageError(e)
          setLoaded(true)
        }}
      />
    </div>
  )
}

// ─── Grid View Components ────────────────────────────────

const GRID_MEDIA_CLASS = 'w-full h-auto object-cover rounded-xl hover:scale-105 transition-transform duration-300'

const GalleryGrid = ({ album, onImageClick }: { album: GalleryAlbum; onImageClick: (url: string) => void }) => {
  const columnCount = 4
  const columns: string[][] = Array.from({ length: columnCount }, () => [])
  const heights = new Array(columnCount).fill(0)

  album.photos.forEach((photo) => {
    const shortest = heights.indexOf(Math.min(...heights))
    columns[shortest].push(buildCdnUrl(photo.url))
    heights[shortest] += 1
  })

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="flex flex-col gap-3">
          {col.map((url, imgIdx) => (
            <div
              key={imgIdx}
              className="rounded-xl cursor-pointer"
              style={{ contentVisibility: 'auto', containIntrinsicSize: '0 200px' }}
            >
              {isVideoUrl(url) ? (
                <video
                  src={url}
                  className={GRID_MEDIA_CLASS}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onClick={() => onImageClick(url)}
                />
              ) : (
                <LazyImage
                  src={url}
                  alt={`${album.title} 사진 ${colIdx * col.length + imgIdx + 1}`}
                  className="rounded-xl"
                  imgClassName={GRID_MEDIA_CLASS}
                  onClick={() => onImageClick(url)}
                />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const AlbumSection = ({ album, onImageClick }: { album: GalleryAlbum; onImageClick: (url: string) => void }) => (
  <section className="flex flex-col gap-5">
    <div className="flex items-end justify-between">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#3B5BDB]">{album.date}</span>
        <h2 className="text-lg md:text-[22px] font-bold text-[#1A1A1A]">{album.title}</h2>
      </div>
      <span className="text-sm text-[#999999]">{album.photoCount}장의 사진</span>
    </div>
    <GalleryGrid album={album} onImageClick={onImageClick} />
  </section>
)

const AllPhotosGrid = ({
  photos,
  hasNext,
  isFetchingMore,
  loadMore,
  onImageClick,
}: {
  photos: GalleryPhotoItem[]
  hasNext: boolean
  isFetchingMore: boolean
  loadMore: () => void
  onImageClick: (url: string) => void
}) => {
  const sentinelRef = useInfiniteScroll(loadMore, {
    enabled: hasNext && !isFetchingMore,
  })

  return (
    <>
      <p className="text-sm text-[#999999] mb-4">
        총 {photos.length}장{hasNext ? '+' : ''}의 사진
      </p>
      <div className="columns-2 md:columns-4 gap-3">
        {photos.map((photo, idx) => {
          const url = buildCdnUrl(photo.url)
          return (
            <div
              key={photo.id}
              className="mb-3 break-inside-avoid rounded-xl cursor-pointer"
              style={{ contentVisibility: 'auto', containIntrinsicSize: '0 250px' }}
            >
              {isVideoUrl(photo.url) ? (
                <video
                  src={url}
                  className={GRID_MEDIA_CLASS}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onClick={() => onImageClick(url)}
                />
              ) : (
                <LazyImage
                  src={url}
                  alt={`갤러리 사진 ${idx + 1}`}
                  className="rounded-xl"
                  imgClassName={GRID_MEDIA_CLASS}
                  onClick={() => onImageClick(url)}
                />
              )}
            </div>
          )
        })}
      </div>
      {isFetchingMore && (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[#3B5BDB] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {!hasNext && photos.length > 0 && (
        <p className="text-center text-sm text-[#999999] py-8">
          모든 사진을 불러왔습니다.
        </p>
      )}
      <div ref={sentinelRef} className="h-1" />
    </>
  )
}

const GridContent = ({
  albums,
  showAllPhotos,
  photos,
  hasNext,
  isFetchingMore,
  loadMore,
  onImageClick,
}: {
  albums: GalleryAlbum[]
  showAllPhotos: boolean
  photos: GalleryPhotoItem[]
  hasNext: boolean
  isFetchingMore: boolean
  loadMore: () => void
  onImageClick: (url: string) => void
}) => (
  <div className="px-4 sm:px-8 lg:px-[60px] py-10">
    <div className="max-w-7xl mx-auto flex flex-col gap-10">
      {showAllPhotos ? (
        <AllPhotosGrid
          photos={photos}
          hasNext={hasNext}
          isFetchingMore={isFetchingMore}
          loadMore={loadMore}
          onImageClick={onImageClick}
        />
      ) : (
        albums.map((album, idx) => (
          <div key={album.id}>
            <AlbumSection album={album} onImageClick={onImageClick} />
            {idx < albums.length - 1 && <div className="h-px bg-[#E0E0E0] mt-10" />}
          </div>
        ))
      )}
    </div>
  </div>
)

// ─── Retreat Sub-Category Section ────────────────────────

const formatRetreatDate = (start: string, end: string) => {
  const s = new Date(start)
  const e = new Date(end)
  const fmt = (d: Date) =>
    `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
  return `${fmt(s)} - ${fmt(e)}`
}

const RetreatHeroBanner = ({
  sub,
  showBrowse,
  onBrowse,
}: {
  sub: SubCategory
  showBrowse: boolean
  onBrowse: () => void
}) => (
  <div className="relative h-[320px] sm:h-[400px] overflow-hidden">
    <img
      src={buildCdnUrl(sub.imageUrl)}
      alt={sub.displayName}
      className="w-full h-full object-cover"
      onError={handleImageError}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 lg:px-[60px] pb-6 sm:pb-8">
      <div className="max-w-7xl mx-auto flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-white/70 tracking-[2px] uppercase">RETREAT</span>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight">
            {sub.displayName}
          </h2>
          <p className="text-sm text-white/80">{formatRetreatDate(sub.startDate, sub.endDate)}</p>
          {sub.verse && (
            <p className="text-sm text-white/70 mt-1">
              {sub.verse.content} ({sub.verse.bookDisplayName} {sub.verse.chapter}장 {sub.verse.verse}절)
            </p>
          )}
        </div>
        {showBrowse && (
          <button
            onClick={onBrowse}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm- font-medium whitespace-nowrap hover:bg-white/30 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
            다른 행사 보기
          </button>
        )}
      </div>
    </div>
  </div>
)

const RetreatSelectorModal = ({
  subCategories,
  selectedSubCategory,
  onSelect,
  onClose,
}: {
  subCategories: SubCategory[]
  selectedSubCategory: string | null
  onSelect: (name: string) => void
  onClose: () => void
}) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-lg mx-4 mb-0 sm:mb-0 bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[80vh] flex flex-col animate-in">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0F0F0]">
        <h3 className="text-lg font-bold text-[#1A1A1A]">수련회 행사 목록</h3>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F0F0F0] transition-colors"
          aria-label="닫기"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto p-4 flex flex-col gap-3">
        {subCategories.map((sub) => {
          const isSelected = selectedSubCategory === sub.name
          return (
            <button
              key={sub.name}
              onClick={() => {
                onSelect(sub.name)
                onClose()
              }}
              className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-200 text-left ${
                isSelected
                  ? 'bg-[#EDF2FF] ring-1 ring-[#3B5BDB]'
                  : 'bg-[#FAFAFA] hover:bg-[#F0F0F0]'
              }`}
            >
              <img
                src={buildCdnUrl(sub.imageUrl)}
                alt={sub.displayName}
                className="w-16 h-20 rounded-lg object-cover flex-shrink-0"
                onError={handleImageError}
              />
              <div className="flex flex-col gap-1 min-w-0">
                <span className={`text-[15px] font-bold leading-tight ${isSelected ? 'text-[#3B5BDB]' : 'text-[#1A1A1A]'}`}>
                  {sub.displayName}
                </span>
                <span className="text-[12px] text-[#999999]">
                  {formatRetreatDate(sub.startDate, sub.endDate)}
                </span>
                {sub.verse && (
                  <span className="text-[11px] text-[#777777] leading-snug">
                    {sub.verse.content} ({sub.verse.bookDisplayName} {sub.verse.chapter}장 {sub.verse.verse}절)
                  </span>
                )}
                {isSelected && (
                  <span className="text-[11px] font-semibold text-[#3B5BDB]">현재 보고 있는 행사</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  </div>
)

// ─── Retreat Program Tabs ────────────────────────────────

const ProgramTabs = ({
  programs,
  selectedProgram,
  onSelect,
}: {
  programs: SubCategoryChild[]
  selectedProgram: string | null
  onSelect: (name: string | null) => void
}) => (
  <div className="bg-white border-b border-[#E0E0E0] px-4 sm:px-8 lg:px-[60px] py-3">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide" role="tablist" aria-label="프로그램별 보기">
        <button
          role="tab"
          aria-selected={selectedProgram === null}
          onClick={() => onSelect(null)}
          className={`shrink-0 px-3.5 sm:px-5 py-2 rounded-full text-xs sm:text-sm- font-medium whitespace-nowrap transition-colors ${
            selectedProgram === null
              ? 'bg-[#3B5BDB] text-white font-semibold'
              : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
          }`}
        >
          전체
        </button>
        {programs.map((program) => (
          <button
            key={program.name}
            role="tab"
            aria-selected={selectedProgram === program.name}
            onClick={() => onSelect(program.name)}
            className={`shrink-0 px-3.5 sm:px-5 py-2 rounded-full text-xs sm:text-sm- font-medium whitespace-nowrap transition-colors ${
              selectedProgram === program.name
                ? 'bg-[#3B5BDB] text-white font-semibold'
                : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
            }`}
          >
            {program.displayName}
          </button>
        ))}
      </div>
    </div>
  </div>
)

// ─── Church Selector Section ────────────────────────────

const ChurchBanner = ({
  churchName,
  onBrowse,
}: {
  churchName: string
  onBrowse: () => void
}) => (
  <div className="bg-gradient-to-r from-[#3B5BDB] to-[#5C7CFA] px-4 sm:px-8 lg:px-[60px] py-6">
    <div className="max-w-7xl mx-auto flex items-center justify-between">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-white/70 tracking-[2px] uppercase">CHURCH</span>
        <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-tight">
          {churchName} 성전
        </h2>
      </div>
      <button
        onClick={onBrowse}
        className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm- font-medium whitespace-nowrap hover:bg-white/30 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
        </svg>
        다른 성전 보기
      </button>
    </div>
  </div>
)

const ChurchSelectorModal = ({
  churchOptions,
  selectedChurchId,
  onSelect,
  onClose,
}: {
  churchOptions: ChurchOption[]
  selectedChurchId: string
  onSelect: (churchId: string) => void
  onClose: () => void
}) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-lg mx-4 mb-0 sm:mb-0 bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[80vh] flex flex-col animate-in">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0F0F0]">
        <h3 className="text-lg font-bold text-[#1A1A1A]">성전 목록</h3>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F0F0F0] transition-colors"
          aria-label="닫기"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto p-4 grid grid-cols-2 gap-2">
        {churchOptions.map((church) => {
          const isSelected = selectedChurchId === church.id
          return (
            <button
              key={church.id}
              onClick={() => {
                onSelect(church.id)
                onClose()
              }}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200 text-left ${
                isSelected
                  ? 'bg-[#EDF2FF] ring-1 ring-[#3B5BDB]'
                  : 'bg-[#FAFAFA] hover:bg-[#F0F0F0]'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-[#3B5BDB]' : 'bg-[#E0E0E0]'
              }`}>
                <span className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-[#666666]'}`}>
                  {church.name.charAt(0)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className={`text-[14px] font-bold leading-tight ${isSelected ? 'text-[#3B5BDB]' : 'text-[#1A1A1A]'}`}>
                  {church.name}
                </span>
                {isSelected && (
                  <span className="text-[11px] font-semibold text-[#3B5BDB]">선택됨</span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  </div>
)

// ─── Feed Video Player ──────────────────────────────────

const MutedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
)

const UnmutedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
)

const FeedVideoPlayer = ({ src, onVideoClick }: { src: string; onVideoClick: (url: string) => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isMuted, setIsMuted] = useState(true)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {/* autoplay blocked */})
          } else {
            video.pause()
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(video)
    return () => observer.disconnect()
  }, [])

  const toggleMute = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setIsMuted(video.muted)
  }, [])

  return (
    <div className="relative w-full h-[360px] sm:h-[400px] cursor-pointer" onClick={() => onVideoClick(src)}>
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        loop
        muted
        playsInline
        preload="metadata"
      />
      <button
        className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white"
        onClick={(e) => {
          e.stopPropagation()
          toggleMute()
        }}
        aria-label={isMuted ? '소리 켜기' : '소리 끄기'}
      >
        {isMuted ? <MutedIcon /> : <UnmutedIcon />}
      </button>
    </div>
  )
}

// ─── Feed View Components ────────────────────────────────

const formatFeedDate = (dateStr: string) => {
  const utcStr = /(Z|[+-]\d{2}(?::\d{2})?)$/.test(dateStr) ? dateStr : dateStr + 'Z'
  const date = new Date(utcStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHour = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return '방금 전'
  if (diffMin < 60) return `${diffMin}분 전`
  if (diffHour < 24) return `${diffHour}시간 전`
  if (diffDay < 7) return `${diffDay}일 전`

  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
}

const FeedImageCarousel = ({ images, onImageClick }: { images: FeedPostImage[]; onImageClick: (url: string) => void }) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const goTo = (idx: number) => {
    if (idx >= 0 && idx < images.length) setCurrentIndex(idx)
  }

  if (images.length === 0) return null

  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, idx) => {
            const url = buildCdnUrl(image.filePath)
            const isVideo = isVideoUrl(url)

            return (
              <div key={image.fileId} className="w-full flex-shrink-0">
                {isVideo ? (
                  idx === currentIndex ? (
                    <FeedVideoPlayer src={url} onVideoClick={onImageClick} />
                  ) : (
                    <video
                      src={url}
                      className="w-full h-[360px] sm:h-[400px] object-cover cursor-pointer"
                      muted
                      preload="metadata"
                      onClick={() => onImageClick(url)}
                    />
                  )
                ) : (
                  <LazyImage
                    src={url}
                    alt={`사진 ${image.sortOrder}`}
                    imgClassName="w-full h-[360px] sm:h-[400px] object-cover cursor-pointer"
                    onClick={() => onImageClick(url)}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={() => goTo(currentIndex - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow-sm hover:bg-white transition-colors"
              aria-label="이전 사진"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button
              onClick={() => goTo(currentIndex + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center shadow-sm hover:bg-white transition-colors"
              aria-label="다음 사진"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Dots indicator */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 py-3">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-[#3B5BDB]' : 'bg-[#D0D0D0]'
              }`}
              aria-label={`사진 ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const GNTC_LOGO_URL = 'https://cdn.gntc-youth.com/assets/gntc-youth-logo-black.webp'
const GNTC_AUTHOR_NAME = 'GNTC YOUTH'

const FeedCard = ({
  post,
  onImageClick,
  isMaster,
  onDeleteClick,
}: {
  post: FeedPost
  onImageClick: (url: string) => void
  isMaster: boolean
  onDeleteClick: (postId: number) => void
}) => {
  const displayName = post.isAuthorPublic ? post.authorName : GNTC_AUTHOR_NAME
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])

  return (
  <article className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden">
    {/* Post header */}
    <div className="flex items-center justify-between px-4 py-3.5">
      <div className="flex items-center gap-3">
        {post.isAuthorPublic ? (
          <ProfileImage
            src={post.authorProfileImageUrl ? buildCdnUrl(post.authorProfileImageUrl) : null}
            alt={post.authorName}
            size={40}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden p-1">
            <img src={GNTC_LOGO_URL} alt="GNTC Youth" className="w-full h-full object-contain" />
          </div>
        )}
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-bold text-[#1A1A1A]">{displayName}</span>
          <span className="text-xs text-[#999999]">{formatFeedDate(post.createdAt)}</span>
        </div>
      </div>
      {isMaster && (
        <div className="relative" ref={menuRef}>
          <button
            className="p-1 text-[#CCCCCC] hover:text-[#999999] transition-colors"
            aria-label="더보기"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-xl shadow-lg border border-[#E0E0E0] overflow-hidden z-10">
              <button
                className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 transition-colors"
                onClick={() => {
                  setMenuOpen(false)
                  onDeleteClick(post.id)
                }}
              >
                삭제
              </button>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Image carousel */}
    <FeedImageCarousel images={post.images} onImageClick={onImageClick} />

    {/* Action bar */}
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-4">
        <button className="hover:opacity-70 transition-opacity" aria-label="좋아요"><HeartIcon /></button>
        <button className="hover:opacity-70 transition-opacity" aria-label="댓글"><CommentIcon /></button>
        <button className="hover:opacity-70 transition-opacity" aria-label="공유"><ShareIcon /></button>
      </div>
      <button className="hover:opacity-70 transition-opacity" aria-label="저장"><BookmarkIcon /></button>
    </div>

    {/* Post footer */}
    <div className="flex flex-col gap-2 px-4 pb-4">
      {post.content && (
        <p className="text-sm- text-[#333333] leading-relaxed">{post.content}</p>
      )}
      {post.hashtags.length > 0 && (
        <span className="text-xs font-medium text-[#3B5BDB]">
          {post.hashtags.map((t) => t.startsWith('#') ? t : `#${t}`).join(' ')}
        </span>
      )}
      {post.commentCount > 0 && (
        <span className="text-xs text-[#999999]">
          댓글 {post.commentCount}개
        </span>
      )}
    </div>
  </article>
  )
}

const DeleteConfirmModal = ({
  onConfirm,
  onCancel,
  isDeleting,
}: {
  onConfirm: () => void
  onCancel: () => void
  isDeleting: boolean
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
    <div role="dialog" aria-modal="true" aria-labelledby="delete-dialog-title" className="relative bg-white rounded-2xl shadow-2xl w-[320px] mx-4 p-6 flex flex-col items-center gap-4 animate-in">
      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      </div>
      <div className="flex flex-col items-center gap-1">
        <h3 id="delete-dialog-title" className="text-lg font-bold text-[#1A1A1A]">게시글 삭제</h3>
        <p className="text-sm text-[#666666] text-center">
          삭제하면 되돌릴 수 없습니다.<br />정말 삭제하시겠습니까?
        </p>
      </div>
      <div className="flex gap-3 w-full mt-2">
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className="flex-1 py-2.5 rounded-lg bg-[#F0F0F0] text-sm font-medium text-[#666666] hover:bg-[#E0E0E0] transition-colors disabled:opacity-50"
        >
          취소
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="flex-1 py-2.5 rounded-lg bg-red-500 text-sm font-medium text-white hover:bg-red-600 transition-colors disabled:opacity-50"
        >
          {isDeleting ? '삭제 중...' : '삭제'}
        </button>
      </div>
    </div>
  </div>
)

const FeedContent = ({
  posts,
  hasNext,
  isFetchingMore,
  loadMore,
  onImageClick,
  isMaster,
  onDeleteClick,
}: {
  posts: FeedPost[]
  hasNext: boolean
  isFetchingMore: boolean
  loadMore: () => void
  onImageClick: (url: string) => void
  isMaster: boolean
  onDeleteClick: (postId: number) => void
}) => {
  const sentinelRef = useInfiniteScroll(loadMore, {
    enabled: hasNext && !isFetchingMore,
  })

  return (
    <div className="flex justify-center px-4 py-10">
      <div className="w-full max-w-[600px] flex flex-col gap-6">
        {posts.map((post) => (
          <FeedCard key={post.id} post={post} onImageClick={onImageClick} isMaster={isMaster} onDeleteClick={onDeleteClick} />
        ))}
        {isFetchingMore && (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#3B5BDB] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!hasNext && posts.length > 0 && (
          <p className="text-center text-sm text-[#999999] py-8">
            모든 피드를 불러왔습니다.
          </p>
        )}
        <div ref={sentinelRef} className="h-1" />
      </div>
    </div>
  )
}

// ─── Media Lightbox ─────────────────────────────────────

const MediaLightbox = ({
  imageUrl,
  onClose,
}: {
  imageUrl: string
  onClose: () => void
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const lightboxVideoRef = useRef<HTMLVideoElement>(null)
  const isVideo = isVideoUrl(imageUrl)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Tab') {
        e.preventDefault()
        closeButtonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    // 라이트박스 열릴 때 갤러리 내 배경 영상 일시정지 (소리 겹침 방지)
    const pausedVideos: HTMLVideoElement[] = []
    const container = document.getElementById('gallery-content')
    container?.querySelectorAll('video').forEach((v) => {
      if (v !== lightboxVideoRef.current && !v.paused) {
        v.pause()
        pausedVideos.push(v)
      }
    })

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''

      // 라이트박스 닫힐 때 화면에 보이는 영상 재개
      pausedVideos.forEach((v) => {
        if (!v.isConnected) return
        const rect = v.getBoundingClientRect()
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
        if (rect.height > 0 && visibleHeight / rect.height >= 0.5) {
          v.play().catch(() => {})
        }
      })
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      data-testid="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isVideo ? '동영상 확대 보기' : '이미지 확대 보기'}
    >
      <button
        ref={closeButtonRef}
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
        aria-label="닫기"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      {isVideo ? (
        <video
          ref={lightboxVideoRef}
          src={imageUrl}
          className="max-w-[95vw] max-h-[95vh] object-contain"
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={imageUrl}
          alt="확대 사진"
          className="max-w-[95vw] max-h-[95vh] object-contain"
          onClick={(e) => e.stopPropagation()}
          onError={handleImageError}
        />
      )}
    </div>
  )
}

// ─── Video Tab ───────────────────────────────────────────

const PlayIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
)

const isValidVideoEmbedUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' &&
      parsed.hostname === 'www.youtube.com' &&
      parsed.pathname.startsWith('/embed/')
  } catch {
    return false
  }
}

// https://www.youtube.com/embed/<id> → 유튜브 썸네일 URL
const getVideoThumbnailUrl = (link: string): string | null => {
  if (!isValidVideoEmbedUrl(link)) return null
  const videoId = new URL(link).pathname.split('/')[2]
  if (!videoId || !/^[\w-]+$/.test(videoId)) return null
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

const buildAutoplayUrl = (link: string): string => {
  const url = new URL(link)
  url.searchParams.set('autoplay', '1')
  return url.toString()
}

const VideoCard = ({ video, onPlay }: { video: EventVideo; onPlay: (video: EventVideo) => void }) => {
  const thumbnailUrl = getVideoThumbnailUrl(video.link)

  return (
    <button
      onClick={() => onPlay(video)}
      aria-label={`${video.title} 영상 재생`}
      className="group flex flex-col gap-2.5 text-left"
    >
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-[#1A1A1A]">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#2B3A67] to-[#1A1A2E]" />
        )}
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/25 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-white pl-1 group-hover:bg-[#3B5BDB] group-hover:scale-110 transition-all duration-200">
            <PlayIcon size={18} />
          </div>
        </div>
      </div>
      <span className="text-sm- sm:text-sm font-semibold text-[#1A1A1A] leading-snug line-clamp-2 group-hover:text-[#3B5BDB] transition-colors">
        {video.title}
      </span>
    </button>
  )
}

const VideoGroupSection = ({
  group,
  selectedProgramKey,
  onSelectProgram,
  onPlay,
}: {
  group: EventVideoGroup
  selectedProgramKey: string | null
  onSelectProgram: (groupKey: string, programKey: string | null) => void
  onPlay: (video: EventVideo) => void
}) => (
  <section className="flex flex-col gap-5">
    <div className="flex items-end justify-between">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#3B5BDB]">VIDEO</span>
        <h2 className="text-lg md:text-[22px] font-bold text-[#1A1A1A]">{group.label}</h2>
      </div>
      <span className="text-sm text-[#999999]">{group.videos.length}개의 영상</span>
    </div>

    {/* 하위 프로그램 칩 (영상이 있는 프로그램만 노출) */}
    {group.programs.length > 0 && (
      <div
        className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mt-1"
        role="group"
        aria-label={`${group.label} 프로그램별 영상 보기`}
      >
        <button
          aria-pressed={selectedProgramKey === null}
          onClick={() => onSelectProgram(group.key, null)}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
            selectedProgramKey === null
              ? 'bg-[#3B5BDB] text-white font-semibold'
              : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
          }`}
        >
          전체
        </button>
        {group.programs.map((program) => (
          <button
            key={program.key}
            aria-pressed={selectedProgramKey === program.key}
            onClick={() => onSelectProgram(group.key, program.key)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              selectedProgramKey === program.key
                ? 'bg-[#3B5BDB] text-white font-semibold'
                : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
            }`}
          >
            {program.label}
          </button>
        ))}
      </div>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-6">
      {group.videos.map((video) => (
        <VideoCard key={video.id} video={video} onPlay={onPlay} />
      ))}
    </div>
  </section>
)

const VideoContent = ({
  groups,
  filteredGroups,
  selectedGroupKey,
  onSelectGroup,
  selectedProgramByGroup,
  onSelectProgram,
  onPlay,
}: {
  groups: EventVideoGroup[]
  filteredGroups: EventVideoGroup[]
  selectedGroupKey: string | null
  onSelectGroup: (key: string | null) => void
  selectedProgramByGroup: Record<string, string | null>
  onSelectProgram: (groupKey: string, programKey: string | null) => void
  onPlay: (video: EventVideo) => void
}) => (
  <div className="px-4 sm:px-8 lg:px-[60px] py-10">
    <div className="max-w-7xl mx-auto flex flex-col gap-8">
      {/* 행사별 필터 칩 */}
      {groups.length > 1 && (
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide" role="group" aria-label="행사별 영상 보기">
          <button
            aria-pressed={selectedGroupKey === null}
            onClick={() => onSelectGroup(null)}
            className={`shrink-0 px-3.5 sm:px-5 py-2 rounded-full text-xs sm:text-sm- font-medium whitespace-nowrap transition-colors ${
              selectedGroupKey === null
                ? 'bg-[#3B5BDB] text-white font-semibold'
                : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
            }`}
          >
            전체
          </button>
          {groups.map((group) => (
            <button
              key={group.key}
              aria-pressed={selectedGroupKey === group.key}
              onClick={() => onSelectGroup(group.key)}
              className={`shrink-0 px-3.5 sm:px-5 py-2 rounded-full text-xs sm:text-sm- font-medium whitespace-nowrap transition-colors ${
                selectedGroupKey === group.key
                  ? 'bg-[#3B5BDB] text-white font-semibold'
                  : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
              }`}
            >
              {group.label}
            </button>
          ))}
        </div>
      )}

      {filteredGroups.map((group, idx) => (
        <div key={group.key}>
          <VideoGroupSection
            group={group}
            selectedProgramKey={selectedProgramByGroup[group.key] ?? null}
            onSelectProgram={onSelectProgram}
            onPlay={onPlay}
          />
          {idx < filteredGroups.length - 1 && <div className="h-px bg-[#E0E0E0] mt-8" />}
        </div>
      ))}
    </div>
  </div>
)

const VideoPlayerModal = ({ video, onClose }: { video: EventVideo; onClose: () => void }) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Tab') {
        e.preventDefault()
        closeButtonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      data-testid="video-player-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${video.title} 영상 재생`}
    >
      <button
        ref={closeButtonRef}
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
        aria-label="영상 닫기"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="w-full max-w-4xl mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="relative w-full bg-black rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
          {isValidVideoEmbedUrl(video.link) ? (
            <iframe
              src={buildAutoplayUrl(video.link)}
              title={video.title}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-[#F0F0F0]">
              <p className="text-sm text-[#999999]">영상을 불러올 수 없습니다.</p>
            </div>
          )}
        </div>
        <p className="mt-3 text-sm font-medium text-white">{video.title}</p>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────

export const GalleryPage = () => {
  const { isLoggedIn, user } = useAuth()
  const [searchParams] = useSearchParams()
  const initialParams = useMemo(() => {
    const category = searchParams.get('category') as GalleryCategory | null
    const churchId = searchParams.get('churchId')
    return {
      category: category && CATEGORIES.some((c) => c.key === category) ? category : undefined,
      churchId: churchId ?? undefined,
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const {
    photos,
    isLoading,
    isFetchingMore,
    error,
    hasNext,
    loadMore,
    selectedCategory,
    setSelectedCategory,
    subCategories,
    selectedSubCategory,
    selectSubCategory,
    selectedProgram,
    selectProgram,
    selectedChurchId,
    selectChurch,
    churchOptions,
  } = useGallery(user?.churchId, initialParams.category, initialParams.churchId)
  const feed = useFeed()
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [showRetreatModal, setShowRetreatModal] = useState(false)
  const [showChurchModal, setShowChurchModal] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const handleCloseLightbox = useCallback(() => setLightboxUrl(null), [])
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [playingVideo, setPlayingVideo] = useState<EventVideo | null>(null)
  const handleCloseVideoPlayer = useCallback(() => setPlayingVideo(null), [])
  const navigate = useNavigate()
  const isMaster = user?.role === 'MASTER'
  const isVideoTab = selectedCategory === 'VIDEO'
  const eventVideos = useEventVideos(isVideoTab)
  // TODO: albums는 카테고리별 뷰에서 사용 - 추후 API 연동
  const albums: GalleryAlbum[] = []

  // 하위 프로그램이 선택되면 해당 프로그램으로, 아니면 수련회 전체로 조회
  const effectiveSubCategory = selectedProgram ?? selectedSubCategory
  const selectedSub = subCategories.find((s) => s.name === selectedSubCategory)
  const programs = selectedSub?.children ?? []

  // 피드 뷰로 전환 시 피드 데이터 로드 (영상 탭은 피드 없음)
  useEffect(() => {
    if (viewMode === 'feed' && !isVideoTab) {
      const opts: { subCategory?: string; churchId?: string } = {}
      if (selectedCategory === 'CHURCH') {
        opts.churchId = selectedChurchId
      } else if (effectiveSubCategory) {
        opts.subCategory = effectiveSubCategory
      }
      feed.loadFeed(opts)
    } else {
      feed.reset()
    }
  }, [viewMode, effectiveSubCategory, selectedCategory, selectedChurchId, isVideoTab, feed.loadFeed, feed.reset])

  const feedLoadMore = useCallback(() => {
    const opts: { subCategory?: string; churchId?: string } = {}
    if (selectedCategory === 'CHURCH') {
      opts.churchId = selectedChurchId
    } else if (effectiveSubCategory) {
      opts.subCategory = effectiveSubCategory
    }
    feed.loadMore(opts)
  }, [feed.loadMore, effectiveSubCategory, selectedCategory, selectedChurchId])

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteTargetId === null) return
    setIsDeleting(true)
    try {
      await deletePost(deleteTargetId)
      feed.removePost(deleteTargetId)
      setDeleteTargetId(null)
    } catch (error) {
      console.error('게시글 삭제 실패:', error)
      alert('게시글 삭제에 실패했습니다.')
    } finally {
      setIsDeleting(false)
    }
  }, [deleteTargetId, feed.removePost])

  const currentIsLoading = isVideoTab
    ? eventVideos.isLoading
    : viewMode === 'feed' ? feed.isLoading : isLoading
  const currentError = isVideoTab
    ? eventVideos.error
    : viewMode === 'feed' ? feed.error : error
  const hasContent = isVideoTab
    ? eventVideos.groups.length > 0
    : viewMode === 'feed'
      ? feed.posts.length > 0
      : photos.length > 0 || albums.length > 0

  return (
    <>
      <Header />
      <main id="gallery-content" className="pt-16 min-h-screen bg-[#F8F9FA]">
        {/* Header Section */}
        <div className="bg-white px-4 sm:px-8 lg:px-[60px] pt-12 pb-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-2">
                <h1 className="text-3xl md:text-[42px] font-extrabold tracking-[4px] text-[#1A1A1A]">
                  GALLERY
                </h1>
                <p className="text-sm text-[#666666]">
                  은혜와진리교회 청년봉사선교회 · 사진 갤러리</p>
              </div>
              {isLoggedIn && !!user?.role && ['LEADER', 'MANAGER', 'MASTER'].includes(user.role) && (
                <button
                  onClick={() => navigate('/gallery/write')}
                  className="px-5 py-2.5 bg-[#3B5BDB] text-white text-sm font-semibold rounded-lg hover:bg-[#364FC7] transition-colors shrink-0"
                >
                  글쓰기
                </button>
              )}
            </div>

            {/* Nav bar: categories + view toggle */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:min-w-0">
                {CATEGORIES.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`shrink-0 px-3.5 sm:px-5 py-2 rounded-full text-xs sm:text-sm- font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === key
                        ? 'bg-[#3B5BDB] text-white font-semibold'
                        : 'bg-[#F0F0F0] text-[#666666] hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {!isVideoTab && (
                <div className="flex shrink-0 justify-end">
                  <ViewToggle viewMode={viewMode} onChange={setViewMode} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Retreat Hero Banner */}
        {selectedCategory === 'RETREAT' && selectedSub && (
          <RetreatHeroBanner
            sub={selectedSub}
            showBrowse={subCategories.length > 1}
            onBrowse={() => setShowRetreatModal(true)}
          />
        )}

        {/* Retreat Program Tabs */}
        {selectedCategory === 'RETREAT' && programs.length > 0 && (
          <ProgramTabs
            programs={programs}
            selectedProgram={selectedProgram}
            onSelect={selectProgram}
          />
        )}

        {/* Retreat Selector Modal */}
        {showRetreatModal && (
          <RetreatSelectorModal
            subCategories={subCategories}
            selectedSubCategory={selectedSubCategory}
            onSelect={selectSubCategory}
            onClose={() => setShowRetreatModal(false)}
          />
        )}

        {/* Church Banner */}
        {selectedCategory === 'CHURCH' && (() => {
          const selected = churchOptions.find((c) => c.id === selectedChurchId)
          return (
            <ChurchBanner
              churchName={selected?.name ?? selectedChurchId}
              onBrowse={() => setShowChurchModal(true)}
            />
          )
        })()}

        {/* Church Selector Modal */}
        {showChurchModal && (
          <ChurchSelectorModal
            churchOptions={churchOptions}
            selectedChurchId={selectedChurchId}
            onSelect={selectChurch}
            onClose={() => setShowChurchModal(false)}
          />
        )}

        {/* Content */}
        {currentIsLoading && (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-2 border-[#3B5BDB] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {currentError && (
          <div className="text-center py-20">
            <p className="text-[#999999] text-sm">{currentError}</p>
          </div>
        )}

        {!currentIsLoading && !currentError && !hasContent && (
          <div className="text-center py-20">
            <p className="text-[#999999] text-sm">
              {isVideoTab
                ? '아직 등록된 영상이 없습니다.'
                : viewMode === 'feed' ? '아직 등록된 피드가 없습니다.' : '아직 등록된 갤러리가 없습니다.'}
            </p>
          </div>
        )}

        {!currentIsLoading && !currentError && hasContent && (
          <div className="transition-opacity duration-300">
            {isVideoTab ? (
              <VideoContent
                groups={eventVideos.groups}
                filteredGroups={eventVideos.filteredGroups}
                selectedGroupKey={eventVideos.selectedGroupKey}
                onSelectGroup={eventVideos.selectGroup}
                selectedProgramByGroup={eventVideos.selectedProgramByGroup}
                onSelectProgram={eventVideos.selectProgram}
                onPlay={setPlayingVideo}
              />
            ) : viewMode === 'grid' ? (
              <GridContent
                albums={albums}
                showAllPhotos={selectedCategory === 'ALL' || selectedCategory === 'CHURCH' || selectedSubCategory !== null}
                photos={photos}
                hasNext={hasNext}
                isFetchingMore={isFetchingMore}
                loadMore={loadMore}
                onImageClick={setLightboxUrl}
              />
            ) : (
              <FeedContent
                posts={feed.posts}
                hasNext={feed.hasNext}
                isFetchingMore={feed.isFetchingMore}
                loadMore={feedLoadMore}
                onImageClick={setLightboxUrl}
                isMaster={isMaster}
                onDeleteClick={setDeleteTargetId}
              />
            )}
          </div>
        )}

        {/* Gallery Footer */}
        <div className="bg-white px-4 sm:px-8 lg:px-[60px] py-10">
          <div className="max-w-7xl mx-auto flex flex-col items-center gap-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E0E0E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <p className="text-base font-medium text-[#1A1A1A] text-center">
              오직 여호와를 경외하는 자는 새 힘을 얻으리니
            </p>
            <span className="text-xs font-semibold text-[#666666] tracking-[2px]">이사야 40:31</span>
            <div className="w-[60px] h-px bg-[#E0E0E0]" />
            <span className="text-[11px] text-[#999999] tracking-[1px]">
              은혜와진리교회 · 청년봉사선교회
            </span>
          </div>
        </div>
      </main>

      {/* Delete Confirm Modal */}
      {deleteTargetId !== null && (
        <DeleteConfirmModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTargetId(null)}
          isDeleting={isDeleting}
        />
      )}

      {/* Image Lightbox */}
      {lightboxUrl && (
        <MediaLightbox imageUrl={lightboxUrl} onClose={handleCloseLightbox} />
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <VideoPlayerModal video={playingVideo} onClose={handleCloseVideoPlayer} />
      )}
    </>
  )
}
